import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { Spinner } from '@phosphor-icons/react'
import { Button } from '@/components/ui'
import { PasswordInput } from '../../../components/input/PasswordInput'
import { cn } from '@/lib/utils'
import { useResetPasswordMutation } from '@/feature/auth/api'
import { useStep, UseStepActions } from '@/lib/hooks/useStep'
import { WizardSuccessScreen } from '@/components/shared/WizardSuccessScreen'
import { LogoExtended } from '@/routes/layouts/public-layout'

const validationSchema = z.object({
  password: z.string().min(8)
})

type FormValues = z.infer<typeof validationSchema>

export const ResetPasswordScreen = (props: UseStepActions) => {
  const { code } = useParams<{ code: string }>()
  const resetPasswordMutation = useResetPasswordMutation()
  const { t } = useTranslation(`wizard`)
  const { register, formState, handleSubmit, setError } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      password: ``
    }
  })
  const onSubmit = handleSubmit(data => {
    if (code) {
      resetPasswordMutation.mutate({
        token: code,
        password: data.password
      })
    }
  })
  const isLoginButtonDisabled = formState.isSubmitting || !formState.isValid || !code || resetPasswordMutation.isPending

  const paswordState = useMemo(() => {
    if (formState.isValid) {
      return `text-success`
    }
    if (formState.errors.password) {
      return `text-destructive`
    }
    return `text-muted-foreground`
  }, [formState.errors.password, formState.isValid])

  useEffect(() => {
    if (resetPasswordMutation.isSuccess) {
      return props.goToNextStep()
    }
    if (resetPasswordMutation.isError) {
      setError(`root`, {
        message: `Invalid code`
      })
    }
  }, [props, resetPasswordMutation.isError, resetPasswordMutation.isSuccess, setError])

  return (
    <div className='flex h-full flex-col items-center justify-between'>
      <LogoExtended />
      <div className='w-full max-w-md'>
        <h1 className='text-center text-4xl font-medium'>{t(`login.screens.reset.title`)}</h1>
        <p className='mx-auto max-w-sm text-center text-lg text-secondary'>{t(`login.screens.reset.description`)}</p>
        <div id='kc-form'>
          <div id='kc-form-wrapper'>
            <form onSubmit={onSubmit} className='mt-10 flex w-full flex-col gap-4'>
              {formState.errors.root ? <p className='text-center text-destructive'>{formState.errors.root.message}</p> : null}
              <div>
                <PasswordInput
                  {...register(`password`)}
                  autoComplete='new-password'
                  className={cn(`rounded-br-none rounded-tr-none border-r-0`, formState.errors.root ? `border-destructive` : `border-gray-300`)}
                />
                <p className={clsx(paswordState, `text-sm`, `mt-2`)}>{t(`sign_up.screens.welcome.form.inputs.password.helper_text`)}</p>
              </div>
              <Button name='reset' type='submit' disabled={isLoginButtonDisabled}>
                {resetPasswordMutation.isPending ? <Spinner className='animate-spin' size={20} /> : t(`login.screens.reset.from.button`)}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <div className='flex items-center justify-center gap-1'>
        <p className='text-muted-foreground'>{t(`sign_up.screens.welcome.login.text`)}</p>
        <Link to='/auth/login' className='primary-link'>
          {t(`sign_up.screens.welcome.login.button`)}
        </Link>
      </div>
    </div>
  )
}

export const ResetPassword = () => {
  const { t } = useTranslation(`wizard`)
  const [current, utils] = useStep(2)
  const navigate = useNavigate()
  const goToDashboard = useCallback(() => navigate(`/auth/login`), [navigate])
  const lastItemUtils = useMemo(() => ({ ...utils, goToNextStep: goToDashboard }), [goToDashboard, utils])

  return (
    <div className='h-full w-full bg-white p-8'>
      {current === 1 && <ResetPasswordScreen {...utils} />}
      {current === 2 && (
        <WizardSuccessScreen actions={lastItemUtils}>
          <div>
            <h1 className='text-center text-4xl font-medium'>{t(`login.screens.reset.success.title`)}</h1>
            <p className='mt-1 text-center text-muted-foreground'>{t(`login.screens.reset.success.description`)}</p>
            <Button asChild className='mt-2'>
              <Link to='/auth/login'>{t(`login.screens.reset.success.button`)}</Link>
            </Button>
          </div>
        </WizardSuccessScreen>
      )}
    </div>
  )
}
